import React, { useState, useEffect, useCallback } from "react";
import moment from "moment";
import SearchLocationInput from "../components/autoComplete";

export default function StepOne({
	eventInfo,
	quoteDetails,
	setQuoteDetails,
	setStepState,
}) {
	const [postcodeValid, setPostcodeValid] = useState(false);
	const [postcodeError, setPostcodeError] = useState(false);
	const [notSelectedError, setNotSelectedError] = useState(false);

	const handleEventDetailsInput = (e) => {
		const { value, name } = e.target;
		setQuoteDetails({ [name]: value });
	};

	useEffect(() => {
		checkFormComplete();
	}, [checkFormComplete, quoteDetails]);

	const checkFormComplete = useCallback(() => {
		const input = document.querySelector("input[name=partyDate]");
		let dateValid;
		input?.classList.remove("input-error");

		if (
			moment(quoteDetails.partyDate, "YYYY-MM-DD", true).isValid() ||
			moment(quoteDetails.partyDate, "DD/MM/YYYY", true).isValid()
		) {
			dateValid = true;
		} else if (
			!dateValid &&
			quoteDetails.partyDate.length > 7 &&
			!moment(quoteDetails.partyDate, "YYYY-MM-DD", true).isValid()
		) {
			input.classList.add("input-error");
		}

		if (isFormValid() && dateValid) {
			setTimeout(() => {
				setStepState(2);
				setQuoteDetails({
					partyDate: formatDate(quoteDetails.partyDate),
				});
			}, 800);
		}
	}, [isFormValid, quoteDetails.partyDate, setQuoteDetails, setStepState]);

	const triggerInputError = (type) => {
		if (type === "postcode") {
			setPostcodeError(true);
			setNotSelectedError(false);
		} else if (type === "notSelected") {
			setPostcodeError(false);
			setNotSelectedError(true);
		}
	};

	const formatDate = (date) => {
		return moment(date, ["MM/DD/YYYY", "MM/DD/YYYY", "YYYY-MM-DD"]).format(
			"DD/MM/YYYY"
		);
	};

	const setTodaysDate = () => {
		const date = new Date(),
			year = date.getFullYear(),
			month = (1 + date.getMonth()).toString().padStart(2, "0"),
			day = date.getDate().toString().padStart(2, "0");

		return `${year}-${month}-${day}`;
	};

	const checkDistance = (postcode, address) => {
		const { base_postcode: basePostcode } = eventInfo;
		const distanceMatrix = new window.google.maps.DistanceMatrixService();

		distanceMatrix.getDistanceMatrix(
			{
				origins: [basePostcode],
				destinations: [postcode],
				travelMode: "DRIVING",
				unitSystem: window.google.maps.UnitSystem.IMPERIAL,
			},
			(res, status) => {
				if (status === "OK") {
					const distance = parseInt(
						res.rows[0].elements[0].distance.text.match(/[\d.]+/)[0]
					);
					checkPostcode(distance, address, postcode);
				} else {
					alert(
						"Unfortunately Google Services are down at the moment, please try again later."
					);
				}
			}
		);
	};

	const checkPostcode = (distance, address, postcode) => {
		if (Number(distance) <= Number(eventInfo.max_radius)) {
			setQuoteDetails({
				address: address,
				postcode: postcode.replace(/\s/g, ""),
				distancePrice: getDistancePrice(distance),
			});

			setPostcodeValid(true);
		} else {
			setStepState(0);
		}
	};

	const getDistancePrice = (distance) => {
		let foundRange, distanceTotal;

		const distanceRangeLength = Object.keys(
			eventInfo.distance_range
		).length;

		Object.keys(eventInfo.distance_range).find((range, i) => {
			if (distance <= parseInt(range)) {
				distanceTotal = eventInfo.distance_range[range];
				foundRange = true;
			} else if (distanceRangeLength - 1 === i) {
				distanceTotal = eventInfo.distance_range[range];
				foundRange = true;
			}

			return foundRange;
		});

		return parseInt(distanceTotal);
	};

	const isFormValid = useCallback(() => {
		const { partyType, partyDate, partyGuests, partyStandSeat } =
			quoteDetails;
		const isValid = [
			partyType.length > 0,
			partyDate.length > 0,
			partyGuests.length > 0,
			partyStandSeat.length > 0,
		];

		return !isValid.some((x) => x === false);
	}, [quoteDetails]);

	const { heading, description_text } = eventInfo;

	return (
		<div className="step-one">
			<div className="step-one__info-container">
				<h4>{heading}</h4>
				{description_text ? <p>{description_text}</p> : null}
			</div>

			<form
				className="step-one__form"
				onSubmit={(e) => e.preventDefault()}
			>
				{!postcodeValid ? (
					<div className="step-one__postcode">
						<SearchLocationInput
							apiKey={eventInfo.google_api_key}
							handler={checkDistance}
							invalid={triggerInputError}
							tabIndex={1} // Set the desired tabIndex value for postcode input
						/>
						{postcodeError ? (
							<p className="step-one__postcode--error">
								Please enter a valid postcode
							</p>
						) : null}
						{notSelectedError ? (
							<p className="step-one__postcode--error">
								Please select a postcode from the list
							</p>
						) : null}
					</div>
				) : (
					<div className="step-one__details">
						<h5>We Cover Your Area!</h5>
						<p>{quoteDetails.address}</p>

						<label>
							<p>Type Of Party</p>
							<select
								className="step-one__form-input"
								name="partyType"
								onChange={handleEventDetailsInput}
								value={quoteDetails.partyType}
								required
								tabIndex={2} // Set the desired tabIndex value for party type select
							>
								<option value="" defaultValue disabled>
									Choose type
								</option>
								{eventInfo.party_types.map((type, i) => (
									<option value={type.party_type} key={i}>
										{type.party_type}
									</option>
								))}
							</select>
						</label>

						<div className="step-one__form-group">
							<label>
								<p>Party Date</p>
								<input
									className="step-one__form-input"
									placeholder="DD/MM/YYYY"
									max="2050-01-01"
									min={setTodaysDate()}
									name="partyDate"
									onChange={handleEventDetailsInput}
									type="date"
									maxLength={10}
									value={quoteDetails.partyDate}
									tabIndex={3} // Set the desired tabIndex value for party date input
								/>
								<p className="partyDate-error">
									Please enter date in (DD/MM/YYYY)
								</p>
							</label>
							<label>
								<p>Party Guests</p>
								<input
									className="step-one__form-input"
									max="70"
									min="1"
									name="partyGuests"
									onChange={handleEventDetailsInput}
									step="1"
									type="number"
									pattern="[0-9]*"
									value={quoteDetails.partyGuests}
									tabIndex={4} // Set the desired tabIndex value for party guests input
								/>
							</label>
						</div>

						<div className="step-one__form-radio">
							<h6 className="step-one__form-radio-helper">
								Are your guests seated or standing?
							</h6>
							<input
								type="radio"
								name="partyStandSeat"
								id="seated"
								value="seated"
								onChange={handleEventDetailsInput}
								checked={
									quoteDetails.partyStandSeat === "seated"
										? "checked"
										: ""
								}
								tabIndex={5} // Set the desired tabIndex value for seated radio button
							/>
							<input
								type="radio"
								name="partyStandSeat"
								id="standing"
								value="standing"
								onChange={handleEventDetailsInput}
								checked={
									quoteDetails.partyStandSeat === "standing"
										? "checked"
										: ""
								}
								tabIndex={6} // Set the desired tabIndex value for standing radio button
							/>

							<div className="step-one__form-radio-switcher">
								<label htmlFor="seated">Seated</label>
								<label htmlFor="standing">Standing</label>
								<span className="step-one__form-radio-switch"></span>
							</div>
						</div>
					</div>
				)}
			</form>
		</div>
	);
}
